import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, User, X } from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { CurrencyPicker } from "@/components/Currency";

const NAV = [
  { to: "/search", label: "Product Search" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Supplier Pricing" },
  { to: "/join-supplier", label: "Join as Supplier" },
];

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
    <img src="/adit-logo.png" alt="ADIT" data-testid="brand-logo-mark"
      className="h-10 w-auto shrink-0 object-contain" />
    <span className="leading-none">
      <span
        className={`block font-display text-[15px] font-black tracking-tight ${dark ? "text-white" : "text-[#102A43]"}`}
      >
        ADIT CONNECT
      </span>
      <span className="block text-[9px] font-semibold uppercase tracking-[0.18em] text-[#667788]">
        Spec Intelligence
      </span>
    </span>
  </Link>
);

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  // Plan B chrome is now shared by every page.

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F7FA]">
      <header className="sticky top-0 z-40 border-b adit-hairline bg-[#0B0F16]/85 backdrop-blur-lg">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-5 py-3.5">
          <Logo />
          <nav className="hidden items-center gap-0.5 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `rounded-[9px] px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive ? "bg-[#eef2f7] text-[#102A43]" : "text-[#667788] hover:text-[#102A43]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 lg:flex">
            <CurrencyPicker compact />
            {user ? (
              <>
                <Link
                  to={homeFor(user.role)}
                  data-testid="header-dashboard-link"
                  className="inline-flex items-center gap-2 rounded-[9px] border border-[#DFE6ED] px-3.5 py-2 text-sm font-semibold text-[#102A43] transition-colors hover:bg-[#eef2f7]"
                >
                  <User className="h-4 w-4" /> {user.role === "admin" ? "Admin" : "My dashboard"}
                </Link>
                <button
                  onClick={signOut}
                  data-testid="header-logout-btn"
                  className="grid h-10 w-10 place-items-center rounded-[9px] border border-[#DFE6ED] text-[#667788] hover:text-[#B42318]"
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="header-login-link"
                  className="adit-lift rounded-[9px] px-3.5 py-2 text-sm font-semibold text-[#667788] transition-colors hover:text-white"
                >
                  Login
                </Link>
                <Link
                  to="/register?role=buyer"
                  data-testid="header-buyer-account-btn"
                  className="adit-lift whitespace-nowrap rounded-[9px] border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-white/25 hover:bg-white/[0.05]"
                >
                  Buyer Account
                </Link>
                <Link
                  to="/rfq"
                  data-testid="header-rfq-btn"
                  className="adit-lift adit-amber-glow whitespace-nowrap rounded-[9px] bg-[#F59E0B] px-4 py-2 text-sm font-bold text-[#1a1204] hover:bg-[#ffab2e]"
                >
                  Submit RFQ — Free
                </Link>
                <Link
                  to="/register?role=supplier"
                  data-testid="header-supplier-btn"
                  className="adit-lift hidden whitespace-nowrap rounded-[9px] border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-2 text-sm font-bold text-[#F59E0B] hover:bg-[#F59E0B]/20 xl:inline-flex"
                >
                  Join as Supplier
                </Link>
              </>
            )}
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="grid h-10 w-10 place-items-center rounded-[9px] border border-[#DFE6ED] lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="border-t adit-hairline bg-[#0F141D] px-5 py-3 lg:hidden" data-testid="mobile-menu">
            {[...NAV, { to: "/rfq", label: "Submit RFQ — Free" },
              { to: "/register?role=supplier", label: "Join as a Founding Supplier" },
              { to: "/register?role=buyer", label: "Create Buyer Account" }].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.slice(1)}`}
                className={`block rounded-[9px] px-3 py-2.5 text-sm font-semibold ${
                  pathname === n.to ? "bg-[#eef2f7] text-[#102A43]" : "text-[#334e68]"
                }`}
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-2 border-t border-[#DFE6ED] pt-2">
              <div className="px-3 pb-2">
                <CurrencyPicker />
              </div>
              {user ? (
                <>
                  <Link
                    to={homeFor(user.role)}
                    onClick={() => setOpen(false)}
                    data-testid="mobile-dashboard-link"
                    className="block rounded-[9px] px-3 py-2.5 text-sm font-semibold text-[#102A43]"
                  >
                    My dashboard
                  </Link>
                  <button
                    onClick={signOut}
                    data-testid="mobile-logout-btn"
                    className="block w-full rounded-[9px] px-3 py-2.5 text-left text-sm font-semibold text-[#B42318]"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-login-link"
                    className="block rounded-[9px] px-3 py-2.5 text-sm font-semibold text-[#102A43]"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register?role=supplier"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-register-link"
                    className="block rounded-[9px] px-3 py-2.5 text-sm font-semibold text-[#102A43]"
                  >
                    Create an account
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-20 border-t adit-hairline bg-[#0F141D]">
        <div className="mx-auto grid max-w-[1240px] gap-8 px-5 py-12 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Logo dark />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#9fb3c8]">
              Industrial specification intelligence and sourcing for the UAE and wider GCC.
              Normalized technical data, certificate-referenced equivalents, structured RFQs
              and landed-cost comparison.
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D9A441]">Buyers</p>
            <ul className="mt-3 space-y-2 text-sm text-[#9fb3c8]">
              <li><Link to="/search" className="hover:text-white">Product search</Link></li>
              <li><Link to="/search" className="hover:text-white">Product &amp; equivalents search</Link></li>
              <li><Link to="/rfq" className="hover:text-white">Submit an RFQ</Link></li>
              <li><Link to="/register?role=buyer" className="hover:text-white">Create buyer account</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D9A441]">Suppliers</p>
            <ul className="mt-3 space-y-2 text-sm text-[#9fb3c8]">
              <li><Link to="/join-supplier" className="hover:text-white">Join as a supplier</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Subscription plans</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white">How it works</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D9A441]">Platform</p>
            <ul className="mt-3 space-y-2 text-sm text-[#9fb3c8]">
              <li>connectadit.com</li>
              <li><Link to="/login" className="hover:text-white">Login</Link></li>
              <li>Oil &amp; Gas · EPC · Power · Marine</li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D9A441]">
              Legal &amp; Support
            </p>
            <ul className="mt-3 space-y-2 text-sm text-[#9fb3c8]">
              <li>
                <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/faqs" data-testid="footer-faqs-link" className="hover:text-white">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/get-help" data-testid="footer-get-help-link" className="hover:text-white">
                  Contact / Support
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 px-5 py-5 text-center text-xs leading-relaxed text-[#829ab1]">
          © {new Date().getFullYear()} ADIT International. Technical data must be verified against the
          manufacturer&apos;s current certificate. Landed costs shown on the platform are estimates.
          On direct purchases the selected supplier is responsible for the product, technical
          conformity, delivery, documentation, warranty and returns.
          <span className="mt-2 block">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span aria-hidden className="px-2">·</span>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <span aria-hidden className="px-2">·</span>
            <Link to="/faqs" className="hover:text-white">FAQs</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
